import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'

interface Props {
  children: ReactNode
  /** One boundary per route, not one for the whole app: a failure in the
   *  attachment list must not blank the visit the doctor is typing. */
  label: string
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Deliberately no remote reporting. Any crash reporter added here would
    // receive component state, and component state here is patient data.
    console.error(`[${this.props.label}]`, error.message, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children
    return (
      <div className="p-5">
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          }
        >
          This part of the screen could not be shown. Nothing you typed has been sent.
        </Banner>
      </div>
    )
  }
}
